<template>
  <transition name="slide-up">
    <div v-if="showConsent" class="cookie-consent">
      <div class="cookie-content">
        <div class="cookie-header">
          <h3>{{ $t('cookie.title') }}</h3>
        </div>
        <p class="cookie-message">{{ $t('cookie.message') }}</p>
        <div class="cookie-actions">
          <button @click="acceptAll" class="btn-primary">
            {{ $t('cookie.acceptAll') }}
          </button>
          <button @click="acceptEssential" class="btn-secondary">
            {{ $t('cookie.essentialOnly') }}
          </button>
          <button @click="showDetails = !showDetails" class="btn-link">
            {{ showDetails ? $t('cookie.hideDetails') : $t('cookie.details') }}
          </button>
        </div>
        <transition name="fade">
          <div v-if="showDetails" class="cookie-details">
            <h4>{{ $t('cookie.detailsTitle') }}</h4>
            <ul>
              <li>
                <strong>{{ $t('cookie.essential') }}</strong>
                <p>{{ $t('cookie.essentialDesc') }}</p>
              </li>
              <li>
                <strong>{{ $t('cookie.analytics') }}</strong>
                <p>{{ $t('cookie.analyticsDesc') }}</p>
              </li>
            </ul>
          </div>
        </transition>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { initAnalytics } from '@/services/analytics'
import { setCookieConsent, getCookieConsent } from '@/services/analytics'
import { env } from '@/config/env'

const showConsent = ref(false)
const showDetails = ref(false)

onMounted(() => {
  const consent = getCookieConsent()
  if (!consent) {
    // 延遲顯示，避免與開場動畫衝突
    setTimeout(() => {
      showConsent.value = true
    }, 2000)
  } else if (consent === 'all' && env.gaMeasurementId) {
    // 如果已經同意，初始化分析
    initAnalytics(env.gaMeasurementId)
  }
})

const acceptAll = () => {
  setCookieConsent('all')
  showConsent.value = false
  
  // 初始化 Google Analytics
  if (env.gaMeasurementId) {
    initAnalytics(env.gaMeasurementId)
  }
}

const acceptEssential = () => {
  setCookieConsent('essential')
  showConsent.value = false
  // 不初始化 Google Analytics
}
</script>

<style scoped>
.cookie-consent {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: var(--z-modal);
  background: var(--bg-card);
  backdrop-filter: blur(20px) saturate(180%);
  border-top: var(--border-width) solid var(--border-color);
  box-shadow: var(--shadow-lg);
  padding: var(--spacing-lg);
}

.cookie-content {
  max-width: 1200px;
  margin: 0 auto;
}

.cookie-header h3 {
  font-size: var(--font-h3-size);
  font-weight: var(--font-h3-weight);
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-sm);
}

.cookie-message {
  color: var(--color-text-secondary);
  margin-bottom: var(--spacing-md);
  line-height: var(--font-body-line);
}

.cookie-actions {
  display: flex;
  gap: var(--spacing-md);
  flex-wrap: wrap;
  margin-bottom: var(--spacing-md);
}

.btn-primary {
  padding: var(--spacing-sm) var(--spacing-lg);
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-hover) 100%);
  color: white;
  border: none;
  border-radius: var(--radius-md);
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-base);
  box-shadow: var(--shadow-primary);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-primary-hover);
}

.btn-secondary {
  padding: var(--spacing-sm) var(--spacing-lg);
  background: transparent;
  color: var(--color-text-primary);
  border: var(--border-width) solid var(--border-color);
  border-radius: var(--radius-md);
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-base);
}

.btn-secondary:hover {
  background: var(--bg-card-hover);
  border-color: var(--border-color-hover);
}

.btn-link {
  padding: var(--spacing-sm) var(--spacing-md);
  background: transparent;
  color: var(--color-primary);
  border: none;
  font-weight: 500;
  cursor: pointer;
  text-decoration: underline;
  transition: all var(--transition-base);
}

.btn-link:hover {
  color: var(--color-primary-hover);
}

.cookie-details {
  margin-top: var(--spacing-md);
  padding-top: var(--spacing-md);
  border-top: var(--border-width) solid var(--border-color);
}

.cookie-details h4 {
  font-size: var(--font-h4-size);
  font-weight: var(--font-h4-weight);
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-sm);
}

.cookie-details ul {
  list-style: none;
  padding: 0;
}

.cookie-details li {
  margin-bottom: var(--spacing-md);
}

.cookie-details strong {
  color: var(--color-primary);
  display: block;
  margin-bottom: var(--spacing-xs);
}

.cookie-details p {
  color: var(--color-text-secondary);
  font-size: var(--font-sm-size);
  line-height: var(--font-sm-line);
}

/* 動畫 */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from {
  opacity: 0;
  transform: translateY(100%);
}

.slide-up-leave-to {
  opacity: 0;
  transform: translateY(100%);
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* 手機版 */
@media (max-width: 768px) {
  .cookie-consent {
    padding: var(--spacing-md);
  }

  .cookie-actions {
    flex-direction: column;
  }

  .btn-primary,
  .btn-secondary {
    width: 100%;
  }
}
</style>

